import React, {useState, useEffect, useRef, useContext} from 'react';
import cn from 'classnames';
import type { IBaseElementProps } from '../BasicElement';
import Button, { ILink } from '../Button';
import Icon from '../Icon';
import Image,{ IImage } from '../Image';
import Nav, { NavItem } from '../Nav';
import Offcanvas, { OffcanvasButton } from '../Offcanvas';
import VHSSearch, {IVHSSearchProps} from '../VHSSearch';
import Dropdown from 'bootstrap/js/dist/dropdown';
import Modal from "../Modal";
import RawHTML from "../RawHTML";
import withAppContext from "../../hooks/withAppContext";
import Loading from "../Loading";
export interface IHeaderProps extends IBaseElementProps {
	/**
	 * Contact Nav Mobile
	 */
	contactNavMobile?: NavItem[];
	/**
	 * Logo
	 */
	logo?: IImage;
	/**
	 * Logo URL
	 */
	logoURL?: ILink;
	/**
	 * Mega Tabs
	 */
	megaTabs?: NavItem[];
	/**
	 * Search Placeholder
	 */
	search?: IVHSSearchProps;
	/**
	 * Primary Nav
	 */
	primaryNav?: NavItem[];
	/**
	 * CTA
	 */
	cta?: ILink;
	/**
	 * Cart
	 */
	cart?: ILink;
	/**
	 * Courses Nav (Mobile Only)
	 */
	coursesNavMobile?: NavItem[];
	/**
	 * Primary Nav (Mobile Only)
	 */
	primaryNavMobile?: NavItem[];
	/**
	 * Secondary Nav
	 */
	secondaryNav?: NavItem[];
	/**
	 * Telephone
	 * @control richtext
	 */
	telephone?: string;
	/**
	 * noUI
	 */
	loginForm?: string;
	/**
	 * noUI
	 */
	code?: string;
	/**
	 * noUI
	 */
	cartTotal?: number;
}

const Header: React.FC<IHeaderProps> = withAppContext(
	({
	context,
	logoURL,
	contactNavMobile,
	logo,
	megaTabs,
	search,
	primaryNav,
	cta,
	cart,
	coursesNavMobile,
	primaryNavMobile,
	secondaryNav,
	telephone,
	loginForm,
	code,
	cartTotal,
	classNames = [],
	className,
}) => {
	const { Api } = useContext(context);
	const [showOffcanvas, setShowOffcanvas] = useState(false);
	const [offCanvasPosition, setOffCanvasPosition] =
		useState<'start' | 'end' | 'top' | 'bottom'>(null);
	const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
	const [enrollingBusy, setEnrollingBusy] = useState(false);
	const handleOffcanvas = () => {
		setShowOffcanvas(!showOffcanvas);
	};

	const [showSearch, setShowSearch] = useState(false);
	const handleOffsearch = () => {
		setShowSearch(!showSearch);
		setShowOffcanvas(false);
	};

	const ref = useRef<HTMLDivElement>(null);

	const handleCTA = () => {
		if (code !== undefined && code !== '') {
			setEnrollingBusy(true);
			// add to the cart
			Api.addCourseToCart([
				{ code: code, fastTrack: '', regionType: '' },
			]).then((results) => {
				//redirect to cart page
				window.location.href = results.url;
				setEnrollingBusy(false);
			});
			return;
		}

		if (cta?.href == '#') {
			// display the "Create Account" modal
			setShowCreateAccountModal(true);
		} else {
			window.open(cta.href, '_self');
		}
	};

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		const initDropdown = () => {
			const dropdownTriggerList = [].slice.call(
				ref.current.querySelectorAll('.dropdown'),
			);

			dropdownTriggerList.map(function (dropdownTriggerEl) {
				return new Dropdown(dropdownTriggerEl);
			});
		};

		const handleOffCanvasBackdrop = () => {
			const backdrop = ref.current.querySelector(
				'.offcanvas-backdrop',
			) as HTMLDivElement;
			const close = ref.current.querySelector(
				'.offcanvas-header .btn-close',
			) as HTMLDivElement;
			backdrop.addEventListener('click', function () {
				setShowOffcanvas(false);
			});
			close.addEventListener('click', function () {
				setShowOffcanvas(false);
			});
		};

		const hamburger = ref.current.querySelector(
			'.vhs-header__hamburger',
		) as HTMLDivElement;
		hamburger.addEventListener('click', handleOffCanvasBackdrop);
		document.addEventListener('DOMContentLoaded', initDropdown);

		return () => {
			hamburger.removeEventListener('click', handleOffCanvasBackdrop);
		};
	}, []);

	function handleOffCanvasPositionOnResize() {
		if (window.innerWidth < 992) {
			setOffCanvasPosition('start');
		} else {
			setOffCanvasPosition('end');
		}
	};

	if (!offCanvasPosition) handleOffCanvasPositionOnResize()

	useEffect(() => {
		const handleOffCanvasNav = () => {
			const items = ref.current?.querySelectorAll(
				'.vhs-header__offcanvas-nav > .nav-item',
			) as NodeListOf<HTMLElement>;

			items && items.forEach((item) => {
				if(item.firstElementChild.classList.contains('collapse-toggle')) {
					item.classList.add('dropdown');
				}
			});
		};

		handleOffCanvasNav();
		window.addEventListener('resize', handleOffCanvasPositionOnResize);

		return () => {
			window.removeEventListener(
				'resize',
				handleOffCanvasPositionOnResize,
			);
		};
	}, [handleOffCanvasPositionOnResize]);

	return (
		<header
			className={className || cn('vhs-header', ...classNames)}
			ref={ref}
		>
			<div className="vhs-header__container">
				{contactNavMobile && contactNavMobile.length > 0 && (
					<div className="vhs-header__contact-nav-wrapper">
						<Nav
							navItems={contactNavMobile}
							collapseEnabled={false}
							dropdownEnabled={true}
							className="vhs-header__contact-nav"
							data-bs-toggle={
								showOffcanvas && !showSearch ? 'offcanvas' : ''
							}
							data-bs-target={
								showOffcanvas && !showSearch
									? '.offcanvas-secondary-menu'
									: ''
							}
						/>
					</div>
				)}
				<div className="vhs-header__logo-wrapper">
					{logo?.src && (
						<Button className="vhs-header__logo" {...logoURL}>
							<Image {...logo} className="img-fluid" />
						</Button>
					)}
				</div>
				{megaTabs && megaTabs.length > 0 && (
					<div className="vhs-header__mega-tabs-wrapper">
						<Nav
							navItems={megaTabs}
							collapseEnabled={false}
							dropdownEnabled={true}
							className="vhs-header__mega-tabs"
						/>
					</div>
				)}
				{search && (
					<div
						className={cn(
							'vhs-header__search-wrapper',
							showSearch && 'vhs-header__search-wrapper--show',
						)}
					>
						<VHSSearch
							{...search}
							closeSearchMobile={setShowSearch}
						/>
						<button
							className={cn(
								'vhs-header__search-button',
								showSearch &&
									'vhs-header__search-button--close',
							)}
							onClick={handleOffsearch}
							data-bs-toggle={
								showOffcanvas && !showSearch ? 'offcanvas' : ''
							}
							aria-label="Search"
							data-bs-target={
								showOffcanvas && !showSearch
									? '.offcanvas-secondary-menu'
									: ''
							}
						>
							<Icon
								name={showSearch ? 'close' : 'magnifying-glass'}
							/>
						</button>
					</div>
				)}
				<div className="vhs-header__primary-nav-wrapper">
					{primaryNav && primaryNav.length > 0 && (
						<Nav
							navItems={primaryNav}
							collapseEnabled={false}
							dropdownEnabled={true}
							className="vhs-header__primary-nav"
						/>
					)}
					{cta?.label && (
						<>
							<Button
								className="vhs-header__button btn btn-sm btn-primary"
								{...cta}
								data-bs-toggle="modal"
								data-bs-target={'#vhs-cta-modal'}
								onClick={() => handleCTA()}
							/>
							{enrollingBusy && <Loading inline={true} />}
						</>
					)}
				</div>
				<div className="vhs-header__cart-wrapper">
					<Button className="vhs-header__cart" {...cart}>
						<Icon name="bag" />
					</Button>
					{ cartTotal > 0 && (
						<div className="cart-amount-wrapper">
							<span className="position-absolute badge rounded-pill bg-primary">{cartTotal}</span>
						</div>
					)}
				</div>
				<div className="vhs-header__hamburger-wrapper">
					<OffcanvasButton
						className={cn(
							'vhs-header__hamburger',
							showOffcanvas && 'vhs-header__hamburger--close',
						)}
						target=".offcanvas-secondary-menu"
						onClick={handleOffcanvas}
					>
						<span>Menu</span>
					</OffcanvasButton>
				</div>
			</div>
			{showCreateAccountModal && (
				<Modal
					id="vhs-cart-remove"
					className="vhs-cart__modal"
					modalClassNames={['modal-dialog-centered']}
					headerClassNames={[]}
					bodyClassNames={[]}
					onCancel={() => setShowCreateAccountModal(false)}
				>
					<RawHTML html={loginForm} />
				</Modal>
			)}
			<Offcanvas
				classNames={['vhs-header__offcanvas offcanvas-secondary-menu']}
				position={offCanvasPosition ? offCanvasPosition : 'start'}
			>
				{coursesNavMobile && coursesNavMobile.length > 0 && (
					<Nav
						navItems={coursesNavMobile}
						collapseEnabled={true}
						dropdownEnabled={false}
						classNames={[
							'vhs-header__offcanvas-nav',
							'vhs-header__offcanvas-nav--courses-nav',
						]}
					/>
				)}
				{primaryNavMobile && primaryNavMobile.length > 0 && (
					<Nav
						navItems={primaryNavMobile}
						collapseEnabled={true}
						dropdownEnabled={false}
						classNames={[
							'vhs-header__offcanvas-nav',
							'vhs-header__offcanvas-nav--primary-nav',
						]}
					/>
				)}
				{secondaryNav && secondaryNav.length > 0 && (
					<Nav
						navItems={secondaryNav}
						collapseEnabled={true}
						dropdownEnabled={false}
						className="vhs-header__offcanvas-nav"
					/>
				)}
				<div className="vhs-header__telephone-wrapper">
					<div
						className="vhs-header__telephone"
						dangerouslySetInnerHTML={{ __html: telephone }}
					/>
					{logo?.src && (
						<Button
							className="vhs-header__telephone-logo"
							{...logoURL}
						>
							<Image {...logo} className="img-fluid" />
						</Button>
					)}
				</div>
			</Offcanvas>
		</header>
	);
},
);
export default Header;
